import { isNotEmpty, parseJson, parseMobile } from '@moka-fe/ats.utils';
import { cloneDeep, get, has, isNumber, omit, pick, setWith } from 'lodash';

import { APPLICATION_ATTACHMENT_TYPE, CURRENT_COUNTRY_CALLING_CODE } from '@/constants/common';

// eslint-disable-next-line max-params
export function candidateToApplyData(
  candidateInfo = {},
  destData,
  customFields = [],
  byref = false,
  preferNewData = false
) {
  if (Object.keys(candidateInfo).length === 0) {
    return destData;
  }
  const newData = byref ? destData : { ...destData };
  // eslint-disable-next-line max-params
  const setResumeData = (candidate, srcData, category, props) => {
    const customFieldIds = Object.keys(candidate)
      .map((x) => Number(x))
      .filter((x) => !isNaN(x));
    props = props.concat(
      customFieldIds.filter((id) =>
        customFields.some((y) => y.id === id && y.relatedTo === category)
      )
    );
    props.forEach((x) => {
      if (!srcData[category]) {
        srcData[category] = { [x]: candidate[x] };
      } else {
        // 是否优先使用新数据
        if (preferNewData) {
          srcData[category][x] = candidate[x] || srcData[category][x];
        } else {
          srcData[category][x] = srcData[category][x] || candidate[x];
        }
      }
    });
  };
  const isObjectEmpty = (x) => Object.keys(x).length === 0;
  const setResumeArrayData = (candidate, srcData, keys) => {
    keys.forEach((key) => {
      let arr = candidate[key] || [];
      if (arr.length) {
        srcData[key] = arr.filter((data) => !isObjectEmpty(data));
        arr.forEach((item) => {
          srcData[key].push(item);
        });
      }
      if (srcData[key]?.length > 1 && candidate[key] instanceof Array) {
        srcData[key] = candidate[key].filter((data) => !isObjectEmpty(data));
      }
    });
  };
  setResumeData(
    candidateInfo,
    newData,
    'basicInfo',
    [
      'callingCode',
      'name',
      'phone',
      'email',
      'gender',
      'age',
      'experience',
      'academicDegree',
      'location',
      'nationality',
      'ethnic',
      'political',
      'lastSpeciality',
      'lastCompany',
      'nativePlace',
      'industry',
      'forwardIndustry',
      'lastUpdate',
      'startFrom',
      'skill',
      'awards',
      'personalInterests',
      'currentJob',
      'fullPhone',
      'graduateDate',
      'citizenId',
      'certificateType',
      'birthDate',
      'idCardSignOrganization',
      'idCardValidity',
      'address',
    ].concat()
  );
  newData.basicInfo.lastCompany =
    newData.basicInfo.lastCompany ||
    ((candidateInfo.experienceInfo || []).length ? candidateInfo.experienceInfo[0].company : '');
  newData.basicInfo.lastSpeciality =
    newData.basicInfo.lastSpeciality ||
    candidateInfo.speciality ||
    ((candidateInfo.educationInfo || []).length ? candidateInfo.educationInfo[0].speciality : '');

  setResumeData(candidateInfo, newData, 'jobIntention', ['salary', 'forwardLocation', 'aimSalary']);
  setResumeData(candidateInfo, newData, 'selfDescription', ['personal']);
  setResumeArrayData(candidateInfo, newData, [
    'educationInfo',
    'experienceInfo',
    'practiceInfo',
    'projectInfo',
    'languageInfo',
    'awardInfo',
  ]);

  if (candidateInfo.resumeKey || candidateInfo.key) {
    newData.uploadInfo.resumeKey = candidateInfo.resumeKey || candidateInfo.key;
    newData.uploadInfo.resumeName = candidateInfo.resumeName || candidateInfo.name;
  }
  if (candidateInfo.attachments) {
    newData.uploadInfo.attachments = candidateInfo.attachments;
  }
  if (candidateInfo.portrait) {
    newData.uploadInfo.portrait = {
      key: candidateInfo.portrait,
      url: candidateInfo.portraitUrl,
    };
  }

  // 处理自定义上传字段
  newData.uploadInfo = {
    ...newData.uploadInfo,
    ...omit(candidateInfo.uploadInfo, [
      'resumeKey',
      'resumeName',
      'attachments',
      'portrait',
      'portraitUrl',
    ]),
  };

  newData.headhunterRecommendInfo = {
    headhunterRecommendComment:
      candidateInfo.headhunterRecommendInfo?.headhunterRecommendComment ?? '',
  };

  // 处理自定义模块
  const customKeys = Object.keys(candidateInfo).filter((key) => key.startsWith('customMulti'));
  setResumeArrayData(candidateInfo, newData, customKeys);
  Object.keys(newData)
    .filter((key) => key.startsWith('customMulti'))
    .forEach((key) => {
      newData[key.substring(11)] = newData[key];
      delete newData[key];
    });

  // 接口中没有返回 fullPhone
  // 推荐的时候phone有可能会携带区号，因为后端认不出部分区号
  newData.basicInfo.fullPhone = (candidateInfo.phone ?? '').startsWith('+')
    ? candidateInfo.phone
    : `+${candidateInfo.countryCallingCode || CURRENT_COUNTRY_CALLING_CODE} ${
        candidateInfo.phone || ''
      }`;

  const [fullCallingCode, phone] = parseMobile(newData.basicInfo.fullPhone, true);

  if (!phone) {
    /**
     * 第一次初始化时 countryCallingCode 和 fullPhone 会被赋予默认值 '+86' '+86 '
     * 此时 preferNewData 为 false 的时候，不会对 fullPhone 更新，导致如果 countryCallingCode 不为 86 会赋值错误
     * 增加一种特殊情况的判断，将目的值 fullPhone 使用传入 fullPhone 覆盖
     * 1.preferNewData 为 false
     * 2.fullCallingCode === newData.basicInfo.countryCallingCode === 86
     * 3.newData.basicInfo.fullPhone ！== candidateInfo.fullPhone 即 candidateInfo.fullPhone 不为 '+86 '
     */

    if (
      !preferNewData &&
      fullCallingCode === newData.basicInfo.countryCallingCode &&
      fullCallingCode === CURRENT_COUNTRY_CALLING_CODE &&
      candidateInfo.fullPhone !== newData.basicInfo.fullPhone
    ) {
      newData.basicInfo.fullPhone = candidateInfo.fullPhone;
    } else {
      const callingCode = newData.basicInfo.countryCallingCode
        ? `+${newData.basicInfo.countryCallingCode} `
        : '';
      newData.basicInfo.fullPhone = callingCode + (newData.basicInfo.phone || '');
    }

    if (!newData.basicInfo.fullPhone) {
      newData.basicInfo.fullPhone = `+${CURRENT_COUNTRY_CALLING_CODE} `;
    }
  }

  return newData;
}

/**
 * 移除提交的表单数据中不显示的字段
 *
 * @param {object} [data={}] 表单数据
 * @param {set} [fieldsNeedToBeRemoved=new Set()] 需要移除的字段
 * @param {boolean} [hasCustomMulti=false] 是否对自定义block做了加前缀处理
 * @returns {object} 处理后的data
 */
export function removeNonDisplayFields(
  data = {},
  fieldsNeedToBeRemoved = new Set(),
  hasCustomMulti = false
) {
  for (let field of fieldsNeedToBeRemoved) {
    field = parseJson(field);
    let { fieldId, blockId, blockIndex } = field;
    // 自定义block
    if (hasCustomMulti && isNumber(blockId)) {
      blockId = `customMulti${blockId}`;
    }
    // multi block
    if (isNotEmpty(blockIndex) && has(data, [blockId, blockIndex, fieldId])) {
      let updatedBlock = get(data, [blockId, blockIndex]);
      delete updatedBlock[fieldId];
      setWith(data, [blockId, blockIndex], updatedBlock);
    }
    // non-multi block
    // 候选人申请表和标准简历有两种数据结构
    // 一种是平铺，一种是和multi block一样放在了数组里
    if (!isNotEmpty(blockIndex)) {
      if (has(data, fieldId)) {
        delete data[fieldId];
      }
      if (has(data, [blockId, fieldId])) {
        delete data[blockId][fieldId];
      }
    }
  }
  return data;
}

// 处理上传模块的数据
export const handleUploadInfoType = (uploadInfo) => {
  if (!uploadInfo) {
    return uploadInfo;
  }
  const newUploadInfo = cloneDeep(uploadInfo);
  const attachmentType = APPLICATION_ATTACHMENT_TYPE.CANDIDATE_UPLOAD_ATTACHMENT;
  const idPhotoType = [
    APPLICATION_ATTACHMENT_TYPE.ID_CARD_FRONT,
    APPLICATION_ATTACHMENT_TYPE.ID_CARD_BACK,
  ];

  Object.entries(newUploadInfo).forEach(([key, info]) => {
    if (Array.isArray(info) && info.length > 0) {
      const newInfo = info.map((attachment) => {
        // 身份证的附件，不需要在这里加type
        if (!idPhotoType.includes(attachment.type)) {
          attachment.type = attachmentType;
        }
        return attachment;
      });
      newUploadInfo[key] = newInfo;
    }
  });

  return newUploadInfo;
};

// java和mage数据结构不一样
export const dealCustomDataForApplyForm = (application) => {
  application.uploadInfo = application.uploadInfoDTO[0];

  const baseField = ['basicInfo', 'jobIntention', 'selfDescription'];
  const arrayField = [
    'educationInfo',
    'experienceInfo',
    'practiceInfo',
    'projectInfo',
    'languageInfo',
    'awardInfo',
  ];
  const dealCustomArrayField = (keys) => {
    keys.forEach((key) => {
      application[key] =
        application[key]?.map((item) => {
          return { ...item, ...parseJson(item?.extend) };
        }) || [];
    });
  };
  dealCustomArrayField(arrayField);
  Object.keys(application.customFields ?? {}).forEach((key) => {
    if (application.customFields[key]?.length) {
      if (arrayField.includes(key)) {
        application.customFields[key].forEach((item) => {
          const currentField = application[key].find((i) => Number(i.id) === Number(item.parentId));
          currentField[item.fieldId] = item.value;
        });
      }
      if (baseField.includes(key)) {
        const fields = {};
        application.customFields[key].forEach((item) => {
          fields[item.fieldId] = item.value;
        });
        application = { ...application, ...fields };
      }
    }
  });

  application = {
    ...application,
    ...pick(application.uploadInfo, [
      'resumeKey',
      'resumeName',
      'attachments',
      // 'portrait',
      // 'portraitUrl',
    ]),
    portrait: application.uploadInfo?.portrait?.key ?? null, // mage的portrait对应Java的portrait.key
    portraitUrl: application.portraitUrl || application.uploadInfo?.portrait?.url || null,
    uploadInfo: {
      ...parseJson(application.uploadInfo?.extend),
    },
  };

  application.idCardValidity = parseJson(application.idCardValidity, []);

  application = { ...application, ...application.applicationKeyMap };

  return application;
};

export const dealCustomDataForUpdateCandidate = (application) => {
  const dealCustomField = (keys) => {
    keys.forEach((key) => {
      if (application[key]) {
        const customKey = Object.keys(application[key]).filter((ik) => !isNaN(Number(ik)));
        application[key] = {
          ...omit(application[key], customKey),
          extend: JSON.stringify({ ...pick(application[key], customKey) }),
        };
      }
    });
  };
  const dealCustomArrayField = (keys) => {
    keys.forEach((key) => {
      application[key] =
        application[key]?.map((item) => {
          const customKey = Object.keys(item).filter((ik) => !isNaN(Number(ik)));
          return {
            ...omit(item, customKey),
            extend: JSON.stringify({ ...pick(item, customKey) }),
          };
        }) || [];
    });
  };
  const dealCustomBlock = () => {
    const customKey = Object.keys(application).filter((key) => !isNaN(Number(key)));
    application = {
      ...omit(application, customKey),
      extend: JSON.stringify(pick(application, customKey)),
    };
  };
  dealCustomField(['basicInfo', 'jobIntention', 'selfDescription', 'uploadInfo']);
  dealCustomArrayField([
    'educationInfo',
    'experienceInfo',
    'practiceInfo',
    'projectInfo',
    'languageInfo',
    'awardInfo',
  ]);
  dealCustomBlock();

  return application;
};

export const delNotRequireField = (applicationInfo, applyFormInfo) => {
  const baseField = ['basicInfo', 'jobIntention', 'selfDescription'];
  const arrayField = [
    'educationInfo',
    'experienceInfo',
    'practiceInfo',
    'projectInfo',
    'languageInfo',
    'awardInfo',
  ];
  const arrayFieldNeedMeta = ['candidateId', 'id', 'orgId'];
  const UPLOAD_FIELD_MAP = {
    RESUME_KEY: 'resumeKey',
    ATTACHMENTS: 'attachments',
    PORTRAIT: 'portrait',
  };
  const { setting } = applyFormInfo;
  let result = {};
  Object.keys(setting).forEach((key) => {
    if (setting[key].show && applicationInfo[key]) {
      // 基础字段
      if (baseField.includes(key)) {
        result[key] = pick(applicationInfo[key], setting[key].isSelected);
        // phone需要追加区号和全部手机号字段
        if (key === 'basicInfo') {
          if (setting[key].isSelected.includes('phone')) {
            // result[key].callingCode 这次的接口没有这个字段的操作，都用的 countryCallingCode，所以先不处理了
            result[key].countryCallingCode = applicationInfo[key].countryCallingCode;
            result[key].fullPhone = applicationInfo[key].fullPhone;
          }
          // citizenId
          if (setting[key].isSelected.includes('citizenId')) {
            result[key].certificateType = applicationInfo[key].certificateType;
          }
        }
      }
      // 多段字段
      if (arrayField.includes(key)) {
        result[key] = applicationInfo[key].map((item) =>
          pick(item, [...arrayFieldNeedMeta, ...setting[key].isSelected])
        );
      }
      // 自定义块
      if (!isNaN(Number(key))) {
        result[key] = applicationInfo[key].map((item) => pick(item, setting[key].isSelected));
      }
    }
  });
  // 特殊字段
  result.uploadInfo = {};
  if (setting.uploadInfo.show) {
    if (setting.uploadInfo.isSelected.includes(UPLOAD_FIELD_MAP.RESUME_KEY)) {
      result.uploadInfo.resumeKey = applicationInfo.uploadInfo.resumeKey || '';
      result.uploadInfo.resumeName = applicationInfo.uploadInfo.resumeName || '';
    }
    if (setting.uploadInfo.isSelected.includes(UPLOAD_FIELD_MAP.ATTACHMENTS)) {
      result.uploadInfo.attachments = applicationInfo.uploadInfo.attachments || [];
    }
    if (setting.uploadInfo.isSelected.includes(UPLOAD_FIELD_MAP.PORTRAIT)) {
      result.uploadInfo.portrait = applicationInfo.uploadInfo.portrait || null;
    }
    setting.uploadInfo.isSelected
      .filter((fieldId) => !isNaN(Number(fieldId)))
      .forEach((key) => {
        if (applicationInfo.uploadInfo[key]) {
          result.uploadInfo[key] = applicationInfo.uploadInfo[key];
        }
      });
  }

  // 推荐评语
  result.headhunterRecommendInfo = applicationInfo.headhunterRecommendInfo;
  // 隐私协议
  result.mokaPrivacyProtocolContentIdList = applicationInfo.mokaPrivacyProtocolContentIdList;
  result.customPrivacyProtocolContentIdList = applicationInfo.customPrivacyProtocolContentIdList;

  return result;
};

// 处理数据中的'至今'，如果教育经历的endDate返回的是至今，在申请表和登记表配置了起始时间字段的情况下才把至今处理成'';(申请表登记表不允许候选人就读时间填写至今)
export const handleEndDate = (candidate, useApplyForm) => {
  const educationSetting = useApplyForm?.setting?.educationInfo;
  if (
    educationSetting?.show &&
    educationSetting?.isSelected?.includes('endDate') &&
    candidate?.educationInfo?.length
  ) {
    const educationInfo = candidate.educationInfo.map((item) => ({
      ...item,
      endDate: item.endDate === '至今' ? '' : item.endDate,
    }));
    candidate = { ...candidate, educationInfo };
  }
  return candidate;
};
